/**
 * Caps mapping editor (caps-materials Phase 2b) — map a NON_DTF cap product to its ONE S&S blank SKU,
 * reusing the calculator's style + colour search so the operator picks a REAL sku (validated by
 * selection, never free-text). Writes PUT /catalog/:store/products/:id/classification { capBlankSsSku }.
 *
 * The cap stays EXCLUDED + NON_DTF — this only records WHICH blank it draws. That blank then feeds
 * Short&reorder → PO (Phase 2a demand) and, later, the produce-time draw (Phase 3). No placements, no
 * per-variant resolution: a cap Shopify product is generic (one SKU).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SheetFooter } from '@/components/ui/sheet';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { blankIdentityLabel, resolveCapVariantSku } from './catalog-lib';
import { pickSizeRows } from './inventory-lib';
import { ColorCombobox } from './screen-print-quote/color-combobox';
import { StyleCombobox, type StylePick } from './screen-print-quote/style-combobox';

type ClassificationView = components['schemas']['ProductClassificationView'];

export function CapsEditorBody({
  storeId,
  productId,
  current,
  onSaved,
}: {
  storeId: string;
  productId: string;
  /** The cap's current classification (its mapped S&S blank + denormalized name), if any. */
  current: ClassificationView | undefined;
  onSaved: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [stylePick, setStylePick] = useState<StylePick | null>(null);
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const style = stylePick?.style ?? '';

  // Same style→variants fetch Add-stock uses (shared React Query cache). Gated on a picked style.
  const variantsQuery = useQuery({
    queryKey: ['style-variants', style],
    enabled: style.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/garments/by-style/{style}/variants', {
        params: { path: { style } },
      });
      if (error)
        throw new Error(error?.error?.detail ?? 'Could not load this style’s colours/sizes.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const variants = variantsQuery.data?.variants ?? [];
  const sizes = pickSizeRows(variants, color);
  const resolvedSku = resolveCapVariantSku(variants, color, size);

  const currentLabel = current
    ? blankIdentityLabel({
        brandName: current.capBlankBrandName,
        styleName: current.capBlankStyleName,
        colorName: current.capBlankColorName,
        sizeName: current.capBlankSizeName,
      })
    : undefined;
  const currentSku = current?.capBlankSsSku;
  // The human label for the pending selection (so the operator sees the blank identity, not just a SKU).
  const pendingLabel = blankIdentityLabel({
    brandName: stylePick?.brand,
    styleName: stylePick?.styleName,
    colorName: color || undefined,
    sizeName: size || undefined,
  });

  const writeClassification = (
    body: {
      capBlankSsSku: string;
      capBlankBrandName: string;
      capBlankStyleName: string;
      capBlankColorName: string;
      capBlankSizeName: string;
    } | null,
  ) =>
    client
      .PUT('/catalog/{storeId}/products/{productId}/classification', {
        params: { path: { storeId, productId } },
        // null clears the whole blank identity; a body sets sku + its denormalized name together.
        body: body ?? {
          capBlankSsSku: null,
          capBlankBrandName: null,
          capBlankStyleName: null,
          capBlankColorName: null,
          capBlankSizeName: null,
        },
      })
      .then(({ data, error }) => {
        if (error) throw new Error(error?.error?.detail ?? 'Save failed.');
        if (!data?.ok) throw new Error('Empty response.');
        return data.data;
      });

  const afterWrite = () => {
    qc.invalidateQueries({ queryKey: ['catalog-detail', storeId, productId] });
    qc.invalidateQueries({ queryKey: ['catalog-caps', storeId] });
    onSaved();
  };

  const save = useMutation({
    mutationFn: () => {
      if (!resolvedSku || !stylePick) throw new Error('Pick a style, colour, and size first.');
      return writeClassification({
        capBlankSsSku: resolvedSku,
        capBlankBrandName: stylePick.brand,
        capBlankStyleName: stylePick.styleName,
        capBlankColorName: color,
        capBlankSizeName: size,
      });
    },
    onSuccess: afterWrite,
  });
  const remove = useMutation({
    mutationFn: () => writeClassification(null),
    onSuccess: afterWrite,
  });

  const busy = save.isPending || remove.isPending;
  const mutError = save.error ?? remove.error;

  return (
    <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-4">
      <Alert>
        <AlertTitle>Cap — produced off the gang sheet</AlertTitle>
        <AlertDescription>
          Map this cap to the S&amp;S blank you order it as. It stays off the DTF sheet; this only
          records which blank to stock, reorder, and (later) draw down when you produce it.
        </AlertDescription>
      </Alert>

      {currentSku && (
        <p className="text-sm">
          Currently mapped to <span className="font-medium">{currentLabel ?? currentSku}</span>
          {currentLabel && (
            <span className="ml-1 font-mono text-xs text-muted-foreground">({currentSku})</span>
          )}
          . Pick a new style/colour/size below to change it.
        </p>
      )}

      <section className="space-y-3">
        <div className="space-y-1.5">
          <Label>Blank style</Label>
          <StyleCombobox
            value={style}
            onPick={(pick) => {
              setStylePick(pick);
              setColor('');
              setSize('');
              save.reset();
              remove.reset();
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Colour</Label>
          <ColorCombobox
            style={style}
            value={color}
            onPick={(c) => {
              setColor(c);
              setSize('');
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Size</Label>
          <Select value={size} onValueChange={setSize} disabled={!color || sizes.length === 0}>
            <SelectTrigger aria-label="Size">
              <SelectValue placeholder={color ? 'Pick a size' : 'Pick a colour first'} />
            </SelectTrigger>
            <SelectContent>
              {sizes.map((v) => (
                <SelectItem key={v.sku} value={v.sizeName}>
                  {v.sizeName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {variantsQuery.isError && (
            <p className="text-xs text-destructive">
              {variantsQuery.error instanceof Error
                ? variantsQuery.error.message
                : 'Could not load colours/sizes.'}
            </p>
          )}
        </div>
        {resolvedSku && (
          <p className="text-xs text-muted-foreground">
            {pendingLabel ? `${pendingLabel} — ` : 'Resolves to '}S&amp;S SKU{' '}
            <span className="font-mono">{resolvedSku}</span>.
          </p>
        )}
      </section>

      {mutError && (
        <Alert variant="destructive">
          <AlertDescription>
            {mutError instanceof Error ? mutError.message : 'Save failed.'}
          </AlertDescription>
        </Alert>
      )}

      <SheetFooter className="mt-auto flex-row justify-between gap-2 px-0">
        {currentSku ? (
          <Button
            type="button"
            variant="outline"
            className="text-destructive"
            disabled={busy}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            Remove blank
          </Button>
        ) : (
          <span />
        )}
        <Button type="button" disabled={busy || !resolvedSku} onClick={() => save.mutate()}>
          {save.isPending && <Loader2 className="size-4 animate-spin" />}
          {currentSku ? 'Update blank' : 'Save blank'}
        </Button>
      </SheetFooter>
    </div>
  );
}
